// src/components/ActionButtons.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../context/AuthContext';
import { CheckInService } from '../services/CheckInService';
import { useNavigate } from 'react-router-dom';
import UploadFootageModal from './modals/UploadFootageModal';
import EditEventModal from './modals/EditEventModal';
import ViewPhotosModal from './modals/ViewPhotosModal';
import API from '../config/config'; // Make sure to import the config

const ActionButtons = ({ event, onCheckIn }) => {
  const { currentUser } = useAuth();
  const [checkedIn, setCheckedIn] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewPhotosModal, setShowViewPhotosModal] = useState(false);
  const [footages, setFootages] = useState([]);
  const [hasFootages, setHasFootages] = useState(false);
  const navigate = useNavigate();

  // Safely extract user id and user type
  const userId = currentUser?.id || currentUser?.user?.id;
  const userType = currentUser?.user_type || currentUser?.user?.user_type;

  useEffect(() => {
    
    // Check for check-in status
    const checkStatus = async () => {
      if (userType === 'event_attendee' && userId && event?.id) {
        const data = await CheckInService.getCheckInStatus(userId, event.id);
        setCheckedIn(data.checked_in);
      }
    };
    
    // Check if the event has footages
    const checkFootages = async () => {
      if (event?.id) {
        try {
          const res = await fetch(`${API.BASE_URL}/api/events/${event.id}/footages`); // Use API.BASE_URL
          const data = await res.json();
          
          const eventFootages = data.footages || [];
          
          setFootages(eventFootages);
          setHasFootages(eventFootages.length > 0);
        } catch (err) {
          console.error("Error checking footages:", err);
          setHasFootages(false);
        }
      }
    };
    
    checkStatus();
    checkFootages();
    
    // Listen for footage upload event
    const handleFootageUploaded = (e) => {
      if (e.detail.eventId === String(event.id)) {
        checkFootages(); // Re-fetch footages
      }
    };
    
    document.addEventListener('footageUploaded', handleFootageUploaded);
    
    // Cleanup the event listener
    return () => {
      document.removeEventListener('footageUploaded', handleFootageUploaded);
    };
  }, [userId, userType, event?.id]);

  if (!currentUser) return null;

  // Check if the user is the event owner and an event promoter
  // Handle the case where event.user_id might be undefined
  const isEventOwner = event.user_id ? parseInt(userId) === parseInt(event.user_id) : true;
  const isEventPromoter = userType === 'event_promoter';


  const handleViewPhotos = async () => {
    try {
      // Use the correct API URL
      const res = await fetch(`${API.BASE_URL}/api/events/${event.id}/footages`);
      const data = await res.json();
      
      // Fix the image URLs to use proper base URL
      const fixedFootages = (data.footages || []).map(footage => ({
        ...footage,
        url: footage.url.startsWith('http') 
          ? footage.url 
          : `${API.BASE_URL}${footage.url}`
      }));
      
      setFootages(fixedFootages);
      setShowViewPhotosModal(true);
    } catch (err) {
      console.error('Error fetching footages:', err);
    }
  };

  if (isEventPromoter && isEventOwner) {
    return (
      <div className="action-buttons d-flex justify-content-end" style={{ marginTop: 10 }}>
        <button
          className="button"
          onClick={() => setShowUploadModal(true)}
          style={{ marginLeft: 10, marginTop: hasFootages ? 0 : 10 }}
        >
          Upload Photos
        </button>
        
        {/* Only show View Photos button if footages exist */}
        {hasFootages && (
          <button
            className="button"
            onClick={handleViewPhotos}
            style={{ marginLeft: 10, marginTop: hasFootages ? 0 : 10 }}
          >
            View Photos
          </button>
        )}
        
        <button
          className="button"
          onClick={() => setShowEditModal(true)}
          style={{ marginLeft: 10, marginTop: hasFootages ? 0 : 10 }}
        >
          Edit Event
        </button>

        {/* Upload Footage Modal - Ensure eventId is passed as a string and not undefined */}
        <UploadFootageModal
          eventId={event?.id ? String(event.id) : ""}
          show={showUploadModal}
          handleClose={() => setShowUploadModal(false)}
        />

        {/* Edit Event Modal */}
        <EditEventModal
          event={event}
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
        />

        {/* View Photos Modal */}
        <ViewPhotosModal
          show={showViewPhotosModal}
          eventPhotos={footages}
          handleClose={() => setShowViewPhotosModal(false)}
        />
      </div>
    );
  }

  // For Event Attendees
  if (userType === 'event_attendee') {
    return (
      <div className="action-buttons d-flex justify-content-end" style={{ marginTop: 10 }}>
        {!checkedIn ? (
          <>
            {/* Always show View Photos button if footages exist */}
            {hasFootages && (
              <button
                className="button me-2"
                onClick={handleViewPhotos}
              >
                View Photos
              </button>
            )}
            
            <button
              className="button"
              onClick={onCheckIn}
            >
              Check In
            </button>
          </>
        ) : (
          <>
            <button
              className="button me-2"
              onClick={() => navigate(`/moments/${event.id}`)}
            >
              Retrieve Moments
            </button>
            
            {/* Show View Photos button for checked-in users too */}
            {hasFootages && (
              <button
                className="button"
                onClick={handleViewPhotos}
              >
                View Photos
              </button>
            )}
          </>
        )}

        {/* View Photos Modal - Available for all attendees */}
        <ViewPhotosModal
          show={showViewPhotosModal}
          eventPhotos={footages}
          handleClose={() => setShowViewPhotosModal(false)}
        />
      </div>
    );
  }

  // For other user types (non-attendees, non-promoters) - Allow them to view photos too
  if (hasFootages) {
    return (
      <div className="action-buttons d-flex justify-content-end" style={{ marginTop: 10 }}>
        <button
          className="button"
          onClick={handleViewPhotos}
        >
          View Photos
        </button>

        {/* View Photos Modal */}
        <ViewPhotosModal
          show={showViewPhotosModal}
          eventPhotos={footages}
          handleClose={() => setShowViewPhotosModal(false)}
        />
      </div>
    );
  }

  // Default return for other user types with no photos
  return null;
};

ActionButtons.propTypes = {
  event: PropTypes.object.isRequired,
  onCheckIn: PropTypes.func.isRequired,
};

export default ActionButtons;
